import en from "./locales/en";
import es from "./locales/es";
import fr from "./locales/fr";
import ja from "./locales/ja";
import vi from "./locales/vi";

export const DEFAULT_LANGUAGE = "en";

export const LANGUAGE_OPTIONS = [
  { id: "en", label: "English" },
  { id: "es", label: "Español" },
  { id: "fr", label: "Français" },
  { id: "ja", label: "日本語" },
  { id: "vi", label: "Tiếng Việt" },
];

const DICTIONARIES = { en, es, fr, ja, vi };
const LANGUAGE_STORAGE_KEY = "aeternaInvicta.language";

export function normalizeLanguage(value) {
  return Object.prototype.hasOwnProperty.call(DICTIONARIES, value)
    ? value
    : DEFAULT_LANGUAGE;
}

export function loadStoredLanguage() {
  try {
    return normalizeLanguage(window.localStorage.getItem(LANGUAGE_STORAGE_KEY));
  } catch (err) {
    return DEFAULT_LANGUAGE;
  }
}

export function saveStoredLanguage(language) {
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, normalizeLanguage(language));
  } catch (err) {}
}

const pluralRulesCache = {};

function pluralCategory(language, count) {
  try {
    if (!pluralRulesCache[language]) {
      pluralRulesCache[language] = new Intl.PluralRules(language);
    }
    return pluralRulesCache[language].select(count);
  } catch (err) {
    return count === 1 ? "one" : "other";
  }
}

function lookup(dictionary, key, count, language) {
  if (typeof count === "number") {
    const plural =
      dictionary[`${key}_${pluralCategory(language, count)}`];
    if (plural != null) return plural;
    const other = dictionary[`${key}_other`];
    if (other != null) return other;
  }
  return dictionary[key];
}

function interpolate(template, params) {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, name) =>
    params[name] == null ? match : String(params[name]),
  );
}

export function translate(language, key, params) {
  const normalized = normalizeLanguage(language);
  const dictionary = DICTIONARIES[normalized];
  const count = params?.count;
  const value =
    lookup(dictionary, key, count, normalized) ??
    lookup(en, key, count, DEFAULT_LANGUAGE) ??
    key;
  return interpolate(value, params);
}

export function createTranslator(language) {
  const normalized = normalizeLanguage(language);
  const translator = (key, params) => translate(normalized, key, params);
  translator.language = normalized;
  return translator;
}

let activeLanguage = DEFAULT_LANGUAGE;

export function setActiveLanguage(language) {
  activeLanguage = normalizeLanguage(language);
}

export function getActiveLanguage() {
  return activeLanguage;
}

export function t(key, params) {
  return translate(activeLanguage, key, params);
}

export function formatDurationMinutes(translator, minutes) {
  const value = Math.max(0, Math.floor(Number(minutes) || 0));
  if (value >= 60 && value % 60 === 0) {
    return translator("duration.hours", { count: value / 60 });
  }
  return translator("duration.minutes", { count: value });
}

export function localizeServerMessageText(entry, translator = t) {
  if (!entry) return "";
  const templateId = entry.templateId;
  if (!templateId) return entry.text || "";
  const key = `serverMessages.template.${templateId}`;
  const params =
    templateId === "maintenanceCountdown"
      ? { duration: formatDurationMinutes(translator, entry.params?.minutes) }
      : entry.params || undefined;
  const localized = translator(key, params);
  if (localized === key) return entry.text || "";
  return entry.note ? `${localized} ${entry.note}` : localized;
}
